import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PlaylistGenerationSettingsService } from '../../../core/services/playlist-generation-settings.service';
import {
  AIProviderName, AIProviderStatus, BackendStorageService, CodexLoginInfo, PlaylistGenerationSettings
} from '../../../core/services/backend-storage.service';

interface ApiKeyInfo {
  hasKey: boolean;
  hint: string | null;
}

interface ApiKeyTestResult {
  valid: boolean;
  message?: string;
}

type AIKeyService = 'gemini' | 'openai';

interface AIKeyProvider {
  service: AIKeyService;
  label: string;
  placeholder: string;
  keyUrl: string;
  keySite: string;
}

@Component({
  selector: 'app-playlist-settings-dialog',
  templateUrl: './playlist-settings-dialog.component.html',
  styleUrls: ['./playlist-settings-dialog.component.scss']
})
export class PlaylistSettingsDialogComponent implements OnInit, OnDestroy {
  settings: PlaylistGenerationSettings;
  originalSettings: PlaylistGenerationSettings;
  selectedTabIndex = 0;
  
  // Validation
  validationErrors: string[] = [];
  
  // Genre options (this would normally come from a service)
  availableGenres = [
    'Pop', 'Rock', 'Hip-Hop', 'Electronic', 'Jazz', 'Classical',
    'Country', 'R&B', 'Reggae', 'Blues', 'Folk', 'Indie',
    'Metal', 'Punk', 'Alternative', 'World', 'Latin', 'Funk'
  ];
  
  // Advanced filters toggles
  enablePopularityFilter = false;
  enableYearFilter = false;
  
  // Year range helpers
  currentYear = new Date().getFullYear();
  tempYearRange = { min: 2000, max: this.currentYear };
  
  // Track count estimation (placeholder)
  trackCountEstimate: number | null = null;
  
  // API Key management (one entry per AI provider)
  readonly aiKeyProviders: AIKeyProvider[] = [
    { service: 'gemini', label: 'Google Gemini', placeholder: 'AIza...',
      keyUrl: 'https://aistudio.google.com/app/apikey', keySite: 'Google AI Studio' },
    { service: 'openai', label: 'OpenAI', placeholder: 'sk-...',
      keyUrl: 'https://platform.openai.com/api-keys', keySite: 'the OpenAI platform' }
  ];
  keyInfo: Record<AIKeyService, ApiKeyInfo | null> = { gemini: null, openai: null };
  providerStatus: AIProviderStatus | null = null;

  // ChatGPT (Codex) sign-in
  codexLogin: CodexLoginInfo | null = null;
  codexBusy = false;
  codexMessage: { ok: boolean; text: string } | null = null;
  private codexPoll: Subscription | null = null;
  newApiKey: Record<AIKeyService, string> = { gemini: '', openai: '' };
  testingKey: Record<AIKeyService, boolean> = { gemini: false, openai: false };
  keyTestResult: Record<AIKeyService, ApiKeyTestResult | null> = { gemini: null, openai: null };

  constructor(
    public dialogRef: MatDialogRef<PlaylistSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private settingsService: PlaylistGenerationSettingsService,
    private backendStorage: BackendStorageService
  ) {
    // Initialize with current settings
    this.settings = this.settingsService.getCurrentSettings();
    this.originalSettings = { ...this.settings };
  }

  ngOnInit(): void {
    this.initializeSettings();
    this.loadApiKeyInfo();
    this.loadProviderStatus();
    
    // Set initial tab if specified in data
    if (this.data?.initialTab) {
      this.selectedTabIndex = this.data.initialTab;
    }
  }

  private initializeSettings(): void {
    // Initialize advanced filter toggles based on current settings
    this.enablePopularityFilter = !!this.settings.popularity;
    this.enableYearFilter = !!this.settings.yearRange;
    
    // Initialize temporary year range
    if (this.settings.yearRange) {
      this.tempYearRange = { ...this.settings.yearRange };
    }
    
    // Ensure popularity object exists for binding
    if (!this.settings.popularity) {
      this.settings.popularity = { min: 20, max: 90 };
    }
  }

  ngOnDestroy(): void {
    this.stopCodexPoll();
  }

  /** The provider whose settings are shown; 'auto'/unset shows the server default. */
  get selectedProvider(): AIProviderName {
    const chosen = this.settings.aiProvider;
    if (chosen && chosen !== 'auto') {
      return chosen;
    }
    return this.providerStatus?.defaultProvider ?? 'gemini';
  }

  get selectedKeyProvider(): AIKeyProvider | undefined {
    return this.aiKeyProviders.find(p => p.service === this.selectedProvider);
  }

  /** Saved right away, so the visible settings always match the provider in use. */
  selectProvider(provider: AIProviderName): void {
    this.settings.aiProvider = provider;
    this.originalSettings = { ...this.originalSettings, aiProvider: provider };
    this.settingsService.updateSettings({ aiProvider: provider }).subscribe({
      error: error => console.error('Failed to save AI provider:', error)
    });
  }

  private loadProviderStatus(): void {
    this.backendStorage.getAIProviderStatus().subscribe({
      next: status => this.applyProviderStatus(status),
      error: error => console.error('Failed to load AI provider status:', error)
    });
  }

  private applyProviderStatus(status: AIProviderStatus): void {
    this.providerStatus = status;
    const codex = status.codex;
    if (codex.signedIn) {
      if (this.codexLogin) {
        this.codexMessage = { ok: true, text: 'Signed in to ChatGPT.' };
      }
      this.codexLogin = null;
      this.stopCodexPoll();
    } else if (codex.pending) {
      // A sign-in is already waiting (e.g. started earlier): show its code and watch for completion
      this.codexLogin = codex.pending;
      this.startCodexPoll();
    } else {
      if (this.codexLogin && codex.error) {
        this.codexMessage = { ok: false, text: codex.error };
      }
      this.codexLogin = null;
      this.stopCodexPoll();
    }
  }

  signInToChatGPT(): void {
    this.codexBusy = true;
    this.codexMessage = null;
    this.backendStorage.startCodexLogin().subscribe({
      next: info => {
        this.codexBusy = false;
        this.codexLogin = info;
        this.startCodexPoll();
      },
      error: error => {
        this.codexBusy = false;
        console.error('Failed to start ChatGPT sign-in:', error);
        this.codexMessage = { ok: false, text: 'Could not start ChatGPT sign-in. Try again.' };
      }
    });
  }

  signOutOfChatGPT(): void {
    this.codexBusy = true;
    this.codexMessage = null;
    this.backendStorage.codexLogout().subscribe({
      next: () => {
        this.codexBusy = false;
        this.codexMessage = { ok: true, text: 'Signed out of ChatGPT.' };
        this.loadProviderStatus();
      },
      error: error => {
        this.codexBusy = false;
        console.error('Failed to sign out of ChatGPT:', error);
        this.codexMessage = { ok: false, text: 'Could not sign out. Try again.' };
      }
    });
  }

  copyCodexCode(): void {
    if (this.codexLogin) {
      void navigator.clipboard?.writeText(this.codexLogin.code);
    }
  }

  private startCodexPoll(): void {
    if (this.codexPoll) {
      return;
    }
    this.codexPoll = timer(3000, 3000).subscribe(() => this.loadProviderStatus());
  }

  private stopCodexPoll(): void {
    this.codexPoll?.unsubscribe();
    this.codexPoll = null;
  }

  private async loadApiKeyInfo(service?: AIKeyService): Promise<void> {
    const services = service ? [service] : this.aiKeyProviders.map(p => p.service);
    await Promise.all(services.map(async s => {
      try {
        const result = await this.backendStorage.getApiKeyInfo(s).toPromise();
        this.keyInfo[s] = result || null;
      } catch (error) {
        console.error(`Failed to load ${s} API key info:`, error);
        this.keyInfo[s] = null;
      }
    }));
  }

  validateAndUpdate(): void {
    this.validationErrors = this.settingsService.getValidationErrors(this.settings);
    
    // Update track count estimate (mock implementation)
    this.updateTrackCountEstimate();
  }

  private updateTrackCountEstimate(): void {
    // Mock track count estimation based on settings
    // In a real implementation, this would call a service
    let estimate = 1000; // Base estimate
    
    if (this.settings.selectedGenres.length > 0) {
      estimate = Math.floor(estimate * (this.settings.selectedGenres.length / 5));
    }
    
    if (this.enablePopularityFilter && this.settings.popularity) {
      const range = this.settings.popularity.max - this.settings.popularity.min;
      estimate = Math.floor(estimate * (range / 100));
    }
    
    if (this.enableYearFilter && this.settings.yearRange) {
      const yearRange = this.settings.yearRange.max - this.settings.yearRange.min;
      estimate = Math.floor(estimate * Math.min(yearRange / 20, 1));
    }
    
    this.trackCountEstimate = Math.max(50, estimate);
  }

  togglePopularityFilter(): void {
    if (this.enablePopularityFilter) {
      this.settings.popularity = { min: 20, max: 90 };
    } else {
      this.settings.popularity = undefined;
    }
    this.validateAndUpdate();
  }

  toggleYearFilter(): void {
    if (this.enableYearFilter) {
      this.settings.yearRange = { ...this.tempYearRange };
    } else {
      this.settings.yearRange = undefined;
    }
    this.validateAndUpdate();
  }

  async testAndSaveApiKey(service: AIKeyService): Promise<void> {
    const apiKey = this.newApiKey[service].trim();
    if (!apiKey) {
      return;
    }

    this.testingKey[service] = true;
    this.keyTestResult[service] = null;

    try {
      const testResult = await this.backendStorage.testApiKey(service, apiKey).toPromise();
      if (testResult && testResult.valid) {
        await this.backendStorage.saveApiKey(service, apiKey).toPromise();
        this.keyTestResult[service] = { valid: true, message: 'API key saved successfully!' };
        this.newApiKey[service] = '';
        await this.loadApiKeyInfo(service);
        this.loadProviderStatus();
      } else {
        this.keyTestResult[service] = {
          valid: false,
          message: testResult?.message || 'Invalid API key - please check your key and try again'
        };
      }
    } catch (error) {
      console.error(`${service} API key test/save failed:`, error);
      this.keyTestResult[service] = { valid: false, message: 'Failed to test API key. Please try again.' };
    } finally {
      this.testingKey[service] = false;
    }
  }

  async removeApiKey(service: AIKeyService): Promise<void> {
    try {
      await this.backendStorage.deleteApiKey(service).toPromise();
      this.keyInfo[service] = { hasKey: false, hint: null };
      this.keyTestResult[service] = null;
      this.loadProviderStatus();
    } catch (error) {
      console.error(`Failed to remove ${service} API key:`, error);
    }
  }

  resetToDefaults(): void {
    this.settingsService.resetToDefaults().subscribe(() => {
      this.settings = this.settingsService.getCurrentSettings();
      this.initializeSettings();
      this.validateAndUpdate();
    });
  }

  onSave(): void {
    this.validateAndUpdate();
    
    if (this.validationErrors.length > 0) {
      return;
    }

    // Update year range from temporary values if year filter is enabled
    if (this.enableYearFilter) {
      this.settings.yearRange = { ...this.tempYearRange };
    }

    this.settingsService.updateSettings(this.settings).subscribe(() => {
      this.dialogRef.close({ saved: true, settings: this.settings });
    });
  }

  onCancel(): void {
    this.dialogRef.close({ saved: false });
  }

  // Helper methods for template
  hasChanges(): boolean {
    return JSON.stringify(this.settings) !== JSON.stringify(this.originalSettings);
  }

  isTabValid(tabIndex: number): boolean {
    switch (tabIndex) {
      case 0: // Basic
        return this.settings.playlistName.trim().length > 0 && 
               this.settings.playlistLength >= 1 && 
               this.settings.playlistLength <= 100;
      case 1: // Filters
        return this.settings.historyFilterDays >= 0 && 
               this.settings.historyFilterDays <= 365 &&
               this.settings.maximumPlayCount >= 1 && 
               this.settings.maximumPlayCount <= 10;
      case 2: // Advanced
        const popularityValid = !this.enablePopularityFilter || 
          (this.settings.popularity && 
           this.settings.popularity.min >= 0 && 
           this.settings.popularity.max <= 100 && 
           this.settings.popularity.min < this.settings.popularity.max);
        
        const yearValid = !this.enableYearFilter || 
          (this.tempYearRange.min >= 1900 && 
           this.tempYearRange.max <= this.currentYear && 
           this.tempYearRange.min < this.tempYearRange.max);
        
        return (popularityValid ?? false) && (yearValid ?? false);
      case 3: // API Keys
        return true; // API keys are optional
      default:
        return true;
    }
  }

  getTabErrorCount(tabIndex: number): number {
    const errors = this.settingsService.getValidationErrors(this.settings);
    
    switch (tabIndex) {
      case 0: // Basic
        return errors.filter(e => 
          e.includes('Playlist') || 
          e.includes('length')
        ).length;
      case 1: // Filters
        return errors.filter(e => 
          e.includes('History') || 
          e.includes('play count')
        ).length;
      case 2: // Advanced
        return errors.filter(e => 
          e.includes('Popularity') || 
          e.includes('Year')
        ).length;
      case 3: // API Keys
        return 0; // API key errors are handled separately
      default:
        return 0;
    }
  }

  // Popularity getters and setters for two-way binding
  getPopularityMin(): number {
    return this.settings.popularity?.min ?? 20;
  }

  setPopularityMin(value: number): void {
    if (!this.settings.popularity) {
      this.settings.popularity = { min: 20, max: 90 };
    }
    this.settings.popularity.min = value;
  }

  getPopularityMax(): number {
    return this.settings.popularity?.max ?? 90;
  }

  setPopularityMax(value: number): void {
    if (!this.settings.popularity) {
      this.settings.popularity = { min: 20, max: 90 };
    }
    this.settings.popularity.max = value;
  }
}